import { deleteJSON, get, postJSON, putJSON } from '../../../utils/Request';
import { adminPermissionList, type AdminPermission } from '../permissions/permissionManageData';

export type PermissionGroupType = 'normal' | 'detail';

export type PermissionGroupFormValues = {
  name: string;
  type: PermissionGroupType;
  enabled: boolean;
  description: string;
  businessCode?: string;
};

export type GroupPermissionItem = {
  id: string;
  name: string;
  resourceType?: AdminPermission['resourceType'] | null;
  resourceValue?: string | null;
  description: string;
  enabled: boolean;
};

export type AdminPermissionGroup = {
  id: string;
  name: string;
  description: string;
  type?: PermissionGroupType;
  businessCode: string;
  enabled: boolean;
  createdAt: string;
  tagLabel: string;
  permissions: GroupPermissionItem[];
};

export type CreateFeatureGroupBody = {
  groupCode: string;
  displayName: string;
  featureNos: string[];
};

export type FeatureGroupFeature = {
  featureNo?: string;
  featureCode?: string;
  displayName?: string;
  status?: string;
};

export type FeatureGroupDetail = {
  groupNo?: string;
  groupCode?: string;
  displayName?: string;
  status?: string;
  features?: FeatureGroupFeature[];
  /** 旧字段；详情已改为 features，提交仍用 featureNos。 */
  featureNos?: string[];
};

export type FeatureGroupDetailContent = {
  data?: FeatureGroupDetail;
  configHash?: string;
};

const FEATURE_NO_PATTERN = /^[0-9]{21}$/;

/** 从详情取成员权限点编号；优先 features，其次兼容 featureNos。 */
export function featureNosFromDetail(detail?: FeatureGroupDetail | null) {
  const fromFeatures = (detail?.features ?? [])
    .map((item) => item.featureNo?.trim() ?? '')
    .filter((item) => FEATURE_NO_PATTERN.test(item));
  if (fromFeatures.length > 0) return fromFeatures;
  return (detail?.featureNos ?? [])
    .map((item) => item.trim())
    .filter((item) => FEATURE_NO_PATTERN.test(item));
}

export type FeatureGroup = {
  groupNo?: string;
  groupCode?: string;
  displayName?: string;
  status?: string;
};

/** GET /api/v1/plan-feature-groups 的 content。 */
export type FeatureGroupPage = {
  records?: FeatureGroup[];
  total?: number;
  pageNum?: number;
  pageSize?: number;
};

/** 分页查询权限分组。displayName 可选，包含匹配；去首尾空格，空白不传。返回体原样交给页面。 */
export function getPlanFeatureGroups(query: { pageNum: number; pageSize: number; displayName?: string }) {
  const displayName = query.displayName?.trim() ?? '';
  return get<FeatureGroupPage>({
    path: '/api/v1/plan-feature-groups',
    params: {
      pageNum: query.pageNum,
      pageSize: query.pageSize,
      ...(displayName ? { displayName } : {})
    }
  });
}

/** 创建权限分组并选择已启用权限点。featureNos 可为空。返回体原样交给页面。 */
export function createPlanFeatureGroup(body: CreateFeatureGroupBody) {
  return postJSON<FeatureGroupDetailContent>({
    path: '/api/v1/plan-feature-groups',
    params: body
  });
}

/** 查询权限分组详情。content.data 为分组，content.configHash 修改时原样传回。 */
export function getPlanFeatureGroupDetail(groupNo: string) {
  return get<FeatureGroupDetailContent>({
    path: `/api/v1/plan-feature-groups/${encodeURIComponent(groupNo)}`
  });
}

export type UpdateFeatureGroupBody = {
  displayName: string;
  status: 'ACTIVE' | 'DISABLED';
  featureNos: string[];
  expectedConfigHash: string;
};

/** 删除未被套餐或权益包引用的权限分组。expectedConfigHash 原样传回详情里的 configHash。被引用或配置变化时返回 409。 */
export function deletePlanFeatureGroup(groupNo: string, expectedConfigHash: string) {
  return deleteJSON<null>({
    path: `/api/v1/plan-feature-groups/${encodeURIComponent(groupNo)}`,
    params: { expectedConfigHash }
  });
}

/** 修改分组名称、启停状态，并用 featureNos 全量替换成员。featureNos 可为空。 */
export function updatePlanFeatureGroup(groupNo: string, body: UpdateFeatureGroupBody) {
  return putJSON<FeatureGroupDetailContent>({
    path: `/api/v1/plan-feature-groups/${encodeURIComponent(groupNo)}`,
    params: body
  });
}

export const GROUP_TYPE_LABEL: Record<PermissionGroupType, string> = {
  normal: '普通权限分组',
  detail: '详情页分组'
};

export const GROUP_TYPE_OPTIONS: { value: PermissionGroupType; label: string }[] = [
  { value: 'normal', label: '普通权限分组' },
  { value: 'detail', label: '详情页分组' }
];

function permItem(
  permissionId: string,
  overrides?: Partial<GroupPermissionItem>
): GroupPermissionItem {
  const base = adminPermissionList.find((p) => p.id === permissionId);
  return {
    id: permissionId,
    name: base?.name ?? overrides?.name ?? '',
    resourceType: base?.resourceType ?? null,
    resourceValue: base?.resourceValue ?? null,
    description: base?.description ?? '',
    enabled: base?.enabled ?? true,
    ...overrides
  };
}

export const adminPermissionGroupList: AdminPermissionGroup[] = [
  {
    id: 'pg-1',
    name: 'Tiktok 商品列表',
    description: 'Tiktok 商品列表相关接口',
    type: 'normal',
    businessCode: '',
    enabled: true,
    createdAt: '2026-02-06T21:57:51',
    tagLabel: '单权权益',
    permissions: [
      permItem('p-8', {
        name: 'tiktok 商品-商品列表',
        resourceType: null,
        resourceValue: null,
        description: 'tiktok 商品-商品列表'
      })
    ]
  },
  {
    id: 'pg-2',
    name: 'Tiktok 商品详情',
    description: 'Tiktok 商品详情相关接口',
    type: 'detail',
    businessCode: 'TIKTOK_PRODUCT_DETAIL',
    enabled: true,
    createdAt: '2026-02-06T21:58:10',
    tagLabel: '单权权益',
    permissions: [permItem('p-9')]
  },
  {
    id: 'pg-3',
    name: 'Tiktok 达人列表',
    description: 'Tiktok 达人列表相关接口',
    type: 'normal',
    businessCode: '',
    enabled: true,
    createdAt: '2026-02-06T21:58:30',
    tagLabel: '单权权益',
    permissions: [permItem('p-2')]
  },
  {
    id: 'pg-4',
    name: 'Tiktok 达人详情',
    description: 'Tiktok 达人详情相关接口',
    type: 'detail',
    businessCode: 'TIKTOK_INFLUENCER_DETAIL',
    enabled: true,
    createdAt: '2026-02-06T21:59:01',
    tagLabel: '单权权益',
    permissions: [permItem('p-3')]
  },
  {
    id: 'pg-5',
    name: 'Tiktok 店铺列表',
    description: 'Tiktok 店铺列表相关接口',
    type: 'normal',
    businessCode: '',
    enabled: true,
    createdAt: '2026-02-06T22:00:11',
    tagLabel: '单权权益',
    permissions: []
  },
  {
    id: 'pg-6',
    name: 'Tiktok 店铺详情',
    description: 'Tiktok 店铺详情相关接口',
    type: 'detail',
    businessCode: 'TIKTOK_SHOP_DETAIL',
    enabled: true,
    createdAt: '2026-02-06T22:01:02',
    tagLabel: '单权权益',
    permissions: []
  },
  {
    id: 'pg-7',
    name: 'Tiktok 视频列表',
    description: 'Tiktok 视频列表相关接口',
    type: 'normal',
    businessCode: '',
    enabled: true,
    createdAt: '2026-02-06T22:02:15',
    tagLabel: '单权权益',
    permissions: [permItem('p-5')]
  },
  {
    id: 'pg-8',
    name: 'Tiktok 直播列表',
    description: 'Tiktok 直播列表相关接口',
    type: 'normal',
    businessCode: '',
    enabled: true,
    createdAt: '2026-02-06T22:03:20',
    tagLabel: '单权权益',
    permissions: [permItem('p-6')]
  },
  {
    id: 'pg-9',
    name: 'Tiktok 类目查询',
    description: 'Tiktok 类目查询相关接口',
    type: 'normal',
    businessCode: '',
    enabled: true,
    createdAt: '2026-02-06T22:04:08',
    tagLabel: '单权权益',
    permissions: [permItem('p-1')]
  },
  {
    id: 'pg-10',
    name: 'Tiktok 粉丝画像',
    description: 'Tiktok 粉丝画像相关接口',
    type: 'detail',
    businessCode: 'TIKTOK_FANS_PORTRAIT',
    enabled: true,
    createdAt: '2026-02-06T22:05:33',
    tagLabel: '单权权益',
    permissions: [permItem('p-7')]
  },
  {
    id: 'pg-11',
    name: '工作台权限组',
    description: '商家工作台菜单与接口',
    type: 'normal',
    businessCode: '',
    enabled: false,
    createdAt: '2026-02-07T09:10:00',
    tagLabel: '单权权益',
    permissions: [permItem('p-11')]
  }
];
